import { useMemo, useState } from 'react';
import type { MasterProductFamily } from 'apis/master-product-families';
import type { EditableFamilyRow } from './editable-family-row.types';
import {
  appendRow,
  buildBulkPayload,
  hasUnsavedChanges,
  insertRowAt,
  mapFamilyToRow,
  prependRow,
  removeRowAt,
  validateRows,
} from './row-helpers';

export function useEditableFamiliesRows(initialFamilies: MasterProductFamily[]) {
  const original = useMemo(() => initialFamilies.map(mapFamilyToRow), [initialFamilies]);
  const [rows, setRows] = useState<EditableFamilyRow[]>(original);

  const updateRow = (clientId: string, patch: Partial<EditableFamilyRow>) =>
    setRows((prev) => prev.map((r) => (r.clientId === clientId ? { ...r, ...patch } : r)));

  const insert = (index: number, position: 'above' | 'below') => setRows((prev) => insertRowAt(prev, index, position));
  const remove = (index: number) => setRows((prev) => removeRowAt(prev, index));
  const append = () => setRows((prev) => appendRow(prev));
  const prepend = () => setRows((prev) => prependRow(prev));

  const errors = useMemo(() => validateRows(rows, original), [rows, original]);
  const dirty = useMemo(() => hasUnsavedChanges(rows, original), [rows, original]);

  return {
    rows,
    errors,
    dirty,
    updateRow,
    insert,
    remove,
    append,
    prepend,
    buildPayload: () => buildBulkPayload(rows, original),
  };
}
